import type { ProductDetailInfo as ProductDetailInfoSchema } from '../../../amplienceTypes/schemas/imported/product-detail-info-schema';
import type { ReactElement } from 'react';

import { useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { ProductDetailControl } from './productDetailControl/ProductDetailControl';
import { CompleteTheLook } from '../../shared/recommendations/CompleteTheLook';
import { media } from '~/components/shared/core/styles';
import { useGalleryContext } from '~/utilities/context/dynamic/GalleryContext';
import { useLayoutDesignContext } from '~/utilities/context/dynamic/LayoutDesignContext';
import { useAppContext } from '~/utilities/context/static/AppContext';
import { usePDPContext } from '~/utilities/context/static/PDPContext';
import { SiteType } from '~/utilities/graphql/codegen';
import { addProductToRecentlyViewedLocalStorage, getSiteStyles } from '~/utilities/helpers';

const S = {
  ProductDetailWrapper: styled.div`
    ${({ theme }) => css`
      position: relative;
      pointer-events: all;

      @media ${media(theme).greaterThan('lg')} {
        width: max(
          ${theme.variables.product?.details.width.desktop},
          ${theme?.variables.product?.details.width.min.desktop}
        );

        ${getSiteStyles(theme.siteType, {
          [SiteType.Aaf]: css`
            width: auto;
          `,
        })}
      }
    `}
  `,
};

export const ProductDetailInfo = (props: ProductDetailInfoSchema): ReactElement => {
  const { country } = useAppContext();
  const { product } = usePDPContext();
  const { code } = product;
  const { styleVariantsHoveredIndex, productStyleVariantIndex, styleVariants } =
    useGalleryContext();
  const { completeTheLookProps } = useLayoutDesignContext();

  useEffect(() => {
    if (product.url) {
      addProductToRecentlyViewedLocalStorage(country, product);
    }
  }, [country, product]);

  const productStyleVariantInfo = useMemo(
    () =>
      styleVariantsHoveredIndex !== productStyleVariantIndex && styleVariants
        ? styleVariants[styleVariantsHoveredIndex]
        : undefined,
    [styleVariants, styleVariantsHoveredIndex, productStyleVariantIndex]
  );

  return (
    <S.ProductDetailWrapper data-cs-capture="" data-code={code}>
      <ProductDetailControl {...props} productStyleVariantInfo={productStyleVariantInfo} />
      {completeTheLookProps && <CompleteTheLook {...completeTheLookProps} />}
    </S.ProductDetailWrapper>
  );
};
